import { type Breakpoint, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { breakpoints } from '../theme/breakpoints';
import checkUndefinedNullOrEmpty from '../utils/tools/checkUndefinedNullOrEmpty';

export type Query = 'up' | 'down' | 'between' | 'only';

export type Value = Breakpoint | number;

const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

export enum SizeTo {
    DOWN_TO = 'downTo',
    UP = 'up',
    BETWEEN = 'between',
    ONLY = 'only',
}

export const getMatches = (el: HTMLElement | null | undefined, option: SizeTo, start: Value, end?: Value): boolean => {
    // Prevents SSR issues
    if (typeof window !== 'undefined' && el) {
        const size1 = typeof start === 'string' ? breakpoints[start] : start;
        const size2 = typeof end === 'string' ? breakpoints[end] : end;
        if (option === SizeTo.DOWN_TO) {
            return el.offsetWidth <= size1;
        }
        if (option === SizeTo.BETWEEN) {
            return el.offsetWidth > size1 && el.offsetWidth <= size2;
        }
        if (option === SizeTo.ONLY) {
            let nextBreakpoint = null;
            Object.keys(breakpoints)?.map((key) => {
                if (checkUndefinedNullOrEmpty(nextBreakpoint) && breakpoints[key] > size1) {
                    nextBreakpoint = key;
                }
            });
            return el.offsetWidth > size1 && el.offsetWidth < breakpoints[nextBreakpoint];
        }
        return el.offsetWidth > size1;
    }
    return false;
};

export function useContainerMediaQuery(ref: HTMLDivElement, option: SizeTo, start: Value, end?: Value): boolean {
    const [matches, setMatches] = useState<boolean>(getMatches(ref, option, start, end));

    // Prevent too many rendering using useCallback
    const handleSize = useCallback(() => {
        setMatches(getMatches(ref, option, start, end));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ref?.offsetHeight, ref?.offsetWidth]);

    useIsomorphicLayoutEffect(() => {
        handleSize();

        // Listen matchMedia
        if (window) {
            window.addEventListener('resize', handleSize);
        }

        return () => {
            if (window) {
                window.removeEventListener('resize', handleSize);
            }
        };
    }, [ref?.offsetWidth]);

    return matches;
}

export function useResponsive(ref: HTMLDivElement, query: Query, start?: Value, end?: Value): boolean {
    const mediaUp = useContainerMediaQuery(ref, SizeTo.UP, start);
    const mediaDown = useContainerMediaQuery(ref, SizeTo.DOWN_TO, start);
    const mediaBetween = useContainerMediaQuery(ref, SizeTo.BETWEEN, start, end);
    const mediaOnly = useContainerMediaQuery(ref, SizeTo.ONLY, start);

    if (query === 'up') {
        return mediaUp;
    }

    if (query === 'down') {
        return mediaDown;
    }

    if (query === 'between') {
        return mediaBetween;
    }

    return mediaOnly;
}
// ----------------------------------------------------------------------

type BreakpointOrNull = Breakpoint | null;

export function useWidth(customKeys?: Breakpoint[]) {
    const theme = useTheme();

    const keys = [...(!checkUndefinedNullOrEmpty(customKeys) ? customKeys : theme.breakpoints.keys)].reverse();

    return (
        keys.reduce((output: BreakpointOrNull, key: Breakpoint) => {
            // eslint-disable-next-line react-hooks/rules-of-hooks
            const matches = useMediaQuery(theme.breakpoints.up(key));

            return !output && matches ? key : output;
        }, null) || 'xs'
    );
}

// ----------------------------------------------------------------------

export function useWindowSize() {
    const [windowSize, setWindowSize] = useState({
        width: undefined,
        height: undefined,
    });

    useEffect(() => {
        function handleResize() {
            setWindowSize({
                width: window.innerWidth,
                height: window.innerHeight,
            });
        }
        window.addEventListener('resize', handleResize);
        handleResize();
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    return windowSize;
}
